import React, { useMemo } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { useCurrentToolId } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';

import FormStatus from './FormStatus';
import { FORM_PLUGIN_METADATA, FORM_ANALYTICS } from './helpers/constants';
import {
  getFormDetailsUrl,
  parseFormFieldUrn,
  getFormFieldsFromLayout,
  getFormDateString,
} from './helpers/utils';
import { useCanRemoveReference, useDisplayName } from './hooks';
import BlockRemoveButton from '../../BlockRemoveButton';
import UnstyledAnchor from '../../UnstyledAnchor';

const FormsEntityBlock = ({
  entity: form,
  projectId,
  onRemoveClick,
  onReferenceClickAnalytics,
}) => {
  const { locale } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`),
  );
  const toolId = useCurrentToolId();

  const handleRemoveClick = () => {
    analytics.track(FORM_ANALYTICS.REMOVE, {
      reference_type: FORM_PLUGIN_METADATA.TYPE,
      source: toolId,
    });
    onRemoveClick();
  };

  const questionText = useMemo(() => {
    const { schemaId } = parseFormFieldUrn(form.id);

    if (!schemaId) return null;

    const formFields = getFormFieldsFromLayout(form.pg_form.file.layout);
    const field = formFields.find((field) => field.schema === schemaId);
    return `${field?.prefix}: ${field?.label}`;
  }, [form]);

  const allowRemove = useCanRemoveReference(onRemoveClick, form);
  const displayName = useDisplayName(form);

  return (
    <UnstyledAnchor
      onClick={onReferenceClickAnalytics}
      href={getFormDetailsUrl(projectId, form.type, form.uid)}
      style={[
        {
          display: 'flex',
          alignItems: 'center',
          padding: '16px',
          paddingRight: '0px',
          height: '75px',
          cursor: 'pointer',
          overflow: 'hidden',
          borderBottom: `1px solid ${theme.colors.charcoal100}`,
          ['&:hover .FormsEntityBlock__remove-button']: {
            visibility: 'visible',
          },
        },
      ]}
    >
      <Box flexGrow="1" paddingRight="5px" overflow="hidden">
        <Flex typography="bodyMediumBold">
          <Box typography="ellipsis" maxWidth="inherit" marginRight="8px">
            {displayName}
          </Box>
          {' #'}
          <div>{form.report_num}</div>
        </Flex>
        <Flex alignItems="center" marginTop="4px">
          <Box flexShrink="0" width="100px">
            {getFormDateString(form.report_date, locale)}
          </Box>
          <FormStatus status={form.status} />
          {questionText && (
            <>
              <Divider
                variant={Divider.VARIANTS.VERTICAL}
                color={Divider.COLORS.DARK}
                style={{ margin: '0px 15px 0px 15px' }}
                height={12}
              />
              <Box flexGrow="1" typography="ellipsis" display="inline">
                {questionText}
              </Box>
            </>
          )}
        </Flex>
      </Box>
      {allowRemove && (
        <Flex
          alignItems="center"
          justifyContent="center"
          height="100%"
          paddingRight="15px"
        >
          <BlockRemoveButton
            className="FormsEntityBlock__remove-button"
            style={{
              visibility: 'hidden',
            }}
            onClick={handleRemoveClick}
          />
        </Flex>
      )}
    </UnstyledAnchor>
  );
};

export default FormsEntityBlock;
